/**
 * @file demoMeeting.ts
 * @brief The built-in demo meeting (calm Home, DES 19-10-2026): a short
 *        sample a new user opens from Home or Help to see notes, decisions,
 *        to-dos, speakers and Ask without recording anything.
 *
 * @details
 * Sample data shipped in the bundle. Nothing is fetched or stored, it is never
 * billed and never counts against minutes, and nobody can change it: the page
 * renders it read-only (MeetingDetailView `demo`). The people are invented
 * (example.com addresses). Every evidence anchor points at a transcript row
 * here, so "see where it was said" works the same as in a real meeting; there
 * is no recording, so nothing plays.
 *
 * Ask answers come from `DEMO_ASK` (DemoAskPanel): fixed questions, each
 * answered with the transcript lines it is drawn from. No model call.
 *
 * Related: ./DemoMeetingPage.tsx, ./DemoAskPanel.tsx
 *
 * @author DES
 * @date 20-10-2026
 */

import type { MeetingDetail } from "../company-meetings/schemas.ts";

/** Route id: `/app/meetings/demo` (and /company, /admin). */
export const DEMO_MEETING_ID = "demo";

const SPEAKERS = [
  { id: "spk_0", name: "Sarah Ahmed", email: "sarah.ahmed@example.com" },
  { id: "spk_1", name: "Daniel Price", email: "daniel.price@example.com" },
  { id: "spk_2", name: "Amira Khan", email: "amira.khan@example.com" },
] as const;

type Line = { id: string; spk: 0 | 1 | 2; at: number; end: number; text: string };

const LINES: Line[] = [
  { id: "t1", spk: 0, at: 0, end: 9, text: "Morning both. Quick one today: are we still on for the app launch on the fourteenth of November?" },
  { id: "t2", spk: 1, at: 10, end: 24, text: "Engineering is mostly there. The one open item is the payment screen on Android. I'd want a week of testing after the fix lands." },
  { id: "t3", spk: 2, at: 25, end: 36, text: "Marketing needs a firm date by Friday, otherwise the press list and the launch email slip too." },
  { id: "t4", spk: 0, at: 37, end: 49, text: "Daniel, when does the Android fix land, realistically?" },
  { id: "t5", spk: 1, at: 50, end: 63, text: "Next Wednesday. Then a week of testing takes us to the thirtieth of October, which still leaves two weeks of margin." },
  { id: "t6", spk: 0, at: 64, end: 76, text: "Then let's agree it: we launch on the fourteenth of November, and we only move it if the Android testing finds a blocker." },
  { id: "t7", spk: 2, at: 77, end: 84, text: "Great, I'll lock the press list and send the launch email draft to you both by Thursday." },
  { id: "t8", spk: 1, at: 85, end: 99, text: "One more thing: support asked for a short FAQ before launch. The top questions are about pricing and data privacy." },
  { id: "t9", spk: 0, at: 100, end: 110, text: "Good point. Amira, can your team write the FAQ? Keep it to ten questions." },
  { id: "t10", spk: 2, at: 111, end: 118, text: "Yes, we can have a first version by the twenty-eighth." },
  { id: "t11", spk: 1, at: 119, end: 133, text: "And on pricing, we said the free plan stays as it is for launch. No changes until we see the first month of numbers." },
  { id: "t12", spk: 0, at: 134, end: 143, text: "Agreed, the free plan stays unchanged at launch. We review it in December with real data." },
  { id: "t13", spk: 2, at: 144, end: 156, text: "Last thing from me: the launch video is still waiting on final screenshots from the new Home screen." },
  { id: "t14", spk: 1, at: 157, end: 166, text: "I'll send those today, they're ready on the test build." },
  { id: "t15", spk: 0, at: 167, end: 178, text: "Perfect. So: launch date fixed, FAQ by the twenty-eighth, screenshots today. Same time next week. Thanks both." },
];

const at = (id: string) => {
  const l = LINES.find((x) => x.id === id);
  return l
    ? { startSecs: l.at, endSecs: l.end, segmentId: l.id, quote: l.text, confidence: "exact" as const, source: "quote" as const }
    : null;
};

/** Days from now at 10:00 local, as an ISO date (the to-dos' due dates). */
function dayAt(now: number, days: number): string {
  const d = new Date(now);
  d.setDate(d.getDate() + days);
  d.setHours(10, 0, 0, 0);
  return d.toISOString();
}

/** The demo, dated yesterday 10:00 so it reads as a recent meeting. */
export function demoMeeting(now = Date.now()): MeetingDetail {
  const started = dayAt(now, -1);
  return {
    id: DEMO_MEETING_ID,
    title: "Demo: Product launch check-in",
    started_at: started,
    created_at: started,
    updated_at: started,
    status: "ready",
    tier: "full",
    // Read-only everywhere: the page also passes `demo`.
    is_owner: false,
    can_delete: false,
    organised_by: "Sarah Ahmed",
    recording_url: null,
    total_duration_seconds: 180,
    target_languages: ["en-GB"],
    bookmarks: [],
    participants: SPEAKERS.map((s) => ({ id: s.id, name: s.name, email: s.email, isExternal: false })),
    resolved_speakers: SPEAKERS.map((s) => ({ speakerId: s.id, name: s.name, isExternal: false })),
    transcript: LINES.map((l) => ({
      id: l.id,
      speakerId: SPEAKERS[l.spk].id,
      speaker: SPEAKERS[l.spk].name,
      text: l.text,
      timestamp: `${String(Math.floor(l.at / 60)).padStart(2, "0")}:${String(l.at % 60).padStart(2, "0")}`,
      startSecs: l.at,
      endSecs: l.end,
    })),
    meeting_overview:
      "The team confirmed the 14 November launch, agreed to keep the free plan unchanged at launch, and split the last tasks: an FAQ, the launch email and the video screenshots.",
    meeting_summary:
      "Engineering expects the Android payment fix next Wednesday, followed by a week of testing, which leaves two weeks of margin before launch.\n\nMarketing needed a firm date by Friday; the date is now fixed unless testing finds a blocker. Support asked for a short FAQ on pricing and data privacy, which Amira's team will write.",
    key_decisions: [
      "Launch on 14 November; move it only if Android testing finds a blocker.",
      "The free plan stays unchanged at launch and is reviewed in December with real data.",
    ],
    key_decision_stamps: [64, 134],
    key_decision_anchors: [at("t6"), at("t12")],
    key_points: [
      { point: "The Android payment screen fix lands next Wednesday, then a week of testing.", topic: "Launch readiness", timeInSecs: 50, anchor: at("t5") },
      { point: "Two weeks of margin remain between the end of testing and launch.", topic: "Launch readiness", timeInSecs: 50, anchor: at("t5") },
      { point: "Support wants a ten-question FAQ, mostly on pricing and data privacy.", topic: "Customer support", timeInSecs: 85, anchor: at("t8") },
      { point: "The launch video is waiting on final screenshots of the new Home screen.", topic: "Marketing", timeInSecs: 144, anchor: at("t13") },
    ],
    metrics: {
      version: 1,
      computedAt: started,
      readScore: { score: 86, badge: "GOOD", label: "Clear", detail: "Short turns, plain language." },
      engagement: { score: 78, badge: "GOOD", label: "Balanced", detail: "All three people spoke." },
      sentiment: { score: 81, badge: "GOOD", label: "Positive", detail: "Agreement on every open item." },
      followThrough: { score: 92, badge: "GOOD", label: "Strong", detail: "Every task has an owner and a date." },
    },
    attention_risks:
      "The launch date depends on the Android payment fix landing next Wednesday; a slip there leaves less testing time.",
    todos: [
      {
        id: "demo-todo-1",
        task: "Lock the press list and send the launch email draft",
        assigneeName: "Amira Khan",
        assigneeSpeakerId: "spk_2",
        assigneeEmail: "amira.khan@example.com",
        dueDate: dayAt(now, 2).slice(0, 10),
        completed: false,
        timeInSecs: 77,
        anchor: at("t7"),
      },
      {
        id: "demo-todo-2",
        task: "Write the launch FAQ (ten questions, pricing and privacy first)",
        assigneeName: "Amira Khan",
        assigneeSpeakerId: "spk_2",
        assigneeEmail: "amira.khan@example.com",
        dueDate: dayAt(now, 7).slice(0, 10),
        completed: false,
        timeInSecs: 111,
        anchor: at("t10"),
      },
      {
        id: "demo-todo-3",
        task: "Send the new Home screen screenshots for the launch video",
        assigneeName: "Daniel Price",
        assigneeSpeakerId: "spk_1",
        assigneeEmail: "daniel.price@example.com",
        dueDate: dayAt(now, -1).slice(0, 10),
        completed: true,
        timeInSecs: 157,
        anchor: at("t14"),
      },
      {
        id: "demo-todo-4",
        task: "Ship the Android payment screen fix and start a week of testing",
        assigneeName: "Daniel Price",
        assigneeSpeakerId: "spk_1",
        assigneeEmail: "daniel.price@example.com",
        dueDate: dayAt(now, 1).slice(0, 10),
        completed: false,
        timeInSecs: 50,
        anchor: at("t5"),
      },
    ],
    meeting_mindmap: {
      rootId: "root",
      nodes: [
        { id: "root", label: "Product launch check-in", type: "root", parentId: null },
        { id: "n1", label: "Launch readiness", type: "topic", parentId: "root" },
        { id: "n1a", label: "Launch on 14 November", type: "decision", parentId: "n1" },
        { id: "n1b", label: "Android payment fix next Wednesday", type: "action", parentId: "n1" },
        { id: "n1c", label: "Android fix could slip", type: "risk", parentId: "n1" },
        { id: "n2", label: "Pricing", type: "topic", parentId: "root" },
        { id: "n2a", label: "Free plan unchanged at launch", type: "decision", parentId: "n2" },
        { id: "n3", label: "Marketing and support", type: "topic", parentId: "root" },
        { id: "n3a", label: "Launch email draft by Thursday", type: "action", parentId: "n3" },
        { id: "n3b", label: "FAQ first version", type: "action", parentId: "n3" },
        { id: "n3c", label: "Screenshots for the video", type: "action", parentId: "n3" },
      ],
    },
  };
}

export type DemoAskItem = {
  question: string;
  answer: string;
  /** Transcript row ids the answer is drawn from. */
  from: string[];
};

/** Ask on the demo: fixed questions, each answered from these lines only. */
export const DEMO_ASK: DemoAskItem[] = [
  {
    question: "When is the launch?",
    answer:
      "On 14 November. Sarah fixed the date and said it only moves if the Android testing finds a blocker.",
    from: ["t6"],
  },
  {
    question: "What is Amira doing before launch?",
    answer:
      "Amira locks the press list and sends the launch email draft by Thursday, and her team writes a ten-question FAQ, first version by the 28th.",
    from: ["t7", "t10"],
  },
  {
    question: "Did we change the free plan?",
    answer:
      "No. The free plan stays unchanged at launch and is reviewed in December with the first month of real numbers.",
    from: ["t11", "t12"],
  },
  {
    question: "What could delay us?",
    answer:
      "The Android payment screen fix. It is due next Wednesday, followed by a week of testing; a slip there eats into the two weeks of margin.",
    from: ["t2", "t5"],
  },
];

/** A transcript line by id, for the Ask citations. */
export function demoLine(id: string): { speaker: string; text: string; at: number } | null {
  const l = LINES.find((x) => x.id === id);
  return l ? { speaker: SPEAKERS[l.spk].name, text: l.text, at: l.at } : null;
}
